<template>
  <button
    class="poi-item"
    :class="{
      loading: isLoading,
      newItem: isNewItem,
      notIncluded: isNotIncludedItem,
    }"
    v-on="$listeners"
    v-bind="$attrs"
  >
    <div class="picture-wrapper">
      <Alert class="removal-alert" :class="{first}" :isActive="isAlertVisible" :position="first ? 'bottom' : 'top'">
        <template v-slot:alert-text>
          <p class="alert-text" v-if="isNewItem">
            Remove from
            <b> Saved List </b>
          </p>
          <p class="alert-text" v-else>
            Remove from
            <b> Saved List & Itinerary</b>
          </p>
        </template>
      </Alert>

      <div class="parent removable" @click.stop="onRemoveItem" @mouseenter="showAlert" @mouseleave="hideAlert">
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="heart-icon"
        >
          <path
            d="M4.99993 1.23985C4.50044 0.752351 3.86237 0.5 3.21539 0.5C2.49175 0.5 1.77927 0.815674 1.25935 1.42301C0.245957 2.60675 0.247904 4.45381 1.25904 5.63724C1.25904 5.63724 1.25904 5.63724 1.25904 5.63724L4.2687 9.15976L4.26903 9.16016C4.35802 9.26409 4.46821 9.34962 4.59406 9.40906L4.80758 8.95694L4.59406 9.40906C4.72005 9.46856 4.85781 9.5 4.99828 9.5C5.13876 9.5 5.27651 9.46856 5.4025 9.40906L5.18899 8.95694L5.4025 9.40906C5.52835 9.34962 5.63854 9.26409 5.72753 9.16016L5.72756 9.16012L8.74018 5.64082C8.74019 5.64082 8.74019 5.64081 8.74019 5.64081C9.75328 4.4574 9.75325 2.60964 8.74019 1.42623C7.74396 0.262468 6.06868 0.200318 4.99993 1.23985Z"
          />
        </svg>

        <div class="container">
          <div v-if="!isLoading" class="img" :style="{backgroundImage: `${backgroundImage}`}"></div>
        </div>
      </div>
    </div>
    <div class="details">
      <div class="details-alligner">
        <div class="description-wrapper">
          <div class="description" v-html="description" />
          <div
            class="poi-state"
            :class="{
              newItem: isNewItem,
              notIncluded: isNotIncludedItem,
            }"
          >
            {{ poiState }}
          </div>
        </div>
        <div class="name">
          <span v-html="formattedName" />
        </div>
      </div>
    </div>
  </button>
</template>

<script>
import Vue from 'vue';
import {truncate} from '@common/place-card-logic/place-card-logic';
import VueCompositionAPI, {computed, ref} from '@vue/composition-api';
import Alert from '@common/alert.vue';
Vue.use(VueCompositionAPI);

export const SavedPoiState = {
  NEW: 'NEW',
  NOT_INCLUDED: 'NOT_INCLUDED',
  NONE: '',
};

export default {
  name: 'SavedPoiItem',
  components: {
    Alert,
  },
  props: {
    activity: Object,
    isLoading: Boolean,
    first: Boolean,
  },
  setup(props, {emit}) {
    const isAlertVisible = ref(false);
    const showAlert = () => {
      isAlertVisible.value = true;
    };
    const hideAlert = () => {
      isAlertVisible.value = false;
    };
    const isLoadingValue = computed(() => {
      return props.isLoading || false;
    });
    const description = computed(() => {
      const type = props.activity.categories.find(Boolean);
      const typrStr = type ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase() : '';
      const s = truncate(props.activity?.description || '', 200, true);
      const address = props.activity?.location?.address || '';
      const exp = (props.activity?.explanations || []).join(', ');
      return isLoadingValue.value ? '' : typrStr || s || exp || address;
    });

    const isNewItem = computed(() => {
      return props.activity?.state === SavedPoiState.NEW;
    });
    const isNotIncludedItem = computed(() => {
      return props.activity?.state === SavedPoiState.NOT_INCLUDED;
    });

    const poiState = computed(() => {
      return isNewItem.value ? 'New' : isNotIncludedItem.value ? 'Not in Itinerary' : '';
    });

    const activityImg = computed(() => {
      if (isLoadingValue.value) {
        return '';
      }

      return props.activity?.images?.small || '';
    });

    const backgroundImage = computed(() => {
      if (isLoadingValue.value) {
        return '';
      }

      return `url('${activityImg.value}')`;
    });

    const formattedName = computed(() => {
      if (isLoadingValue.value) {
        return '';
      }

      const name = (props.activity?.name || '')
        .replace('LUNCH:', '')
        .replace('BREAKFAST:', '')
        .replace('DINNER:', '')
        .trim();
      const type = (props.activity?.name || '').replace(name, '').replace(':', '').trim().toLowerCase();
      const at = type.length ? 'at' : '';

      return `${type && type.length ? type.charAt(0).toUpperCase() + type.slice(1) : ''} ${at} <b>${name}</b>`.trim();
    });

    const onRemoveItem = () => {
      emit('remove-item');
    };

    return {
      onRemoveItem,
      backgroundImage,
      description,
      formattedName,
      isNewItem,
      isNotIncludedItem,
      poiState,
      isAlertVisible,
      showAlert,
      hideAlert,
    };
  },
};
</script>

<style scoped>
.poi-item {
  display: flex;
  padding: 8px 20px 5px;
  border-top: 1px solid #f1f1f1;
  background-size: 16px;
  background-position: 15px center;
  background-repeat: no-repeat;
  transition: 0.3s ease background-color;
  padding-right: 60px;
  position: relative;
  cursor: pointer;
  width: 100%;
  border: none;
  appearance: none;
  align-items: stretch;
  background-color: #fff;
}

.poi-item.newItem {
  background-color: var(--toneBw-5);
}
.poi-item.notIncluded {
  background-color: var(--toneAlert-10);
}

.parent:hover:before {
  opacity: 1;
}

.removable:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L11 11' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M1 11L11 1' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
  background-position: center center;
  background-repeat: no-repeat;
  z-index: 1;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.2s linear;
}

.poi-item::after {
  content: '';
  position: absolute;
  right: 5px;
  width: 40px;
  height: 40px;
  background-size: 8px;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url(./icon-filter-downarrow.svg);
  transform: rotate(-90deg);
  top: 50%;
  margin-top: -20px;
}

.parent {
  width: 100%;
  background-repeat: no-repeat;
  background-position: 50%;
  background-size: cover;
}

.container {
  display: block;
  width: 100%;
  height: auto;
  position: relative;
  overflow: hidden;
  padding: 100% 0 0 0; /* 63.29% = 100 / (w / h) = 100 / (314 / 198) */
}

.container .img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%);
  background-size: cover;
  border-radius: var(--radiusSmall);
}

.picture-wrapper {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 5px;
  flex-shrink: 0;
  display: inline-block;
}

.picture-wrapper > * {
  border-radius: 5px;
}

.details {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.details-alligner {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-left: 10px;
  overflow: hidden;
  flex: 1;
  justify-content: flex-start;
}

.name {
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 118.14%;
  text-align: left;
  padding-right: 30px;
  letter-spacing: -0.005em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: inline-block;
  white-space: nowrap;
  color: #071d3b;
}

.description {
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 118.14%;

  letter-spacing: -0.005em;

  color: rgba(7, 29, 59, 0.41);
  text-align: left;
  padding-right: 25px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
}

.poi-item {
  padding: 4px 20px 4px 4px !important;
}
.poi-item .name {
  font-size: 13px !important;
}
.poi-item .icon {
  font-size: 22px !important;
}

.poi-item::after {
  opacity: 0.4 !important;
}

.loading.poi-item {
  pointer-events: none;
}

.loading.poi-item:after {
  content: none;
}

.loading .picture-wrapper,
.loading .name,
.loading .description {
  background: rgba(228, 229, 232, 0.5);
}

.loading .name,
.loading .description {
  height: 11px;
  width: 30px;
  border-radius: 5px;
}

.loading .name {
  margin-top: 5px;
}

.loading .description {
  margin-top: 5px;
  width: 115px;
}

.loading .details-alligner {
  flex: 1;
}
.heart-icon {
  position: absolute;
  top: -3px;
  right: -3px;
  fill: #ff7373;
  stroke: #fff;
  z-index: 1;
}
.description-wrapper {
  display: flex;
  justify-content: space-between;
  align-self: stretch;
}
.poi-state.newItem,
.poi-state.notIncluded {
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.poi-state.newItem {
  color: var(--newItemState);
}

.poi-state.notIncluded {
  color: var(--toneAlert-100);
}

.removal-alert {
  bottom: calc(100% + 7px);
  z-index: 2;
  padding: 0;
}

.removal-alert.first {
  top: calc(100% + 7px);
  bottom: auto;
}

.alert-text {
  font-size: var(--tSize-xs);
  font-style: normal;
  font-weight: 500;
  line-height: 130%;
}

.alert-text b {
  font-weight: 700;
}
</style>
